import {hpcAttrs} from '@github-ui/hpc'
import type {FilterRef} from '@github-ui/filter'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import type {WorkflowRun} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {useQueryClient} from '@github-ui/react-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {Pagination} from '@primer/react'
import {useCallback, useEffect, useId, useLayoutEffect, useRef} from 'react'
import {useAutoRefreshPreference} from '../../hooks/workflow-runs/use-auto-refresh-preference'
import {useWorkflowRunsSearch} from '../../hooks/workflow-runs/use-workflow-runs-search'
import {DeepPaginationTip} from './DeepPaginationTip'
import {WorkflowRunsFilter} from './WorkflowRunsFilter'
import {WorkflowRunsList} from './WorkflowRunsList'
import {WorkflowRunsLiveUpdates} from './WorkflowRunsLiveUpdates'
import {useActionsAnalytics} from '../../hooks/use-actions-analytics'
import {type AutoRefreshOptionId, getAutoRefreshOption} from './workflow-runs-auto-refresh'

// Page # to start showing ProTip
const DEEP_PAGINATION_TIP_PAGE = 10

interface WorkflowRunsPageProps {
  owner: string
  repo: string
  workflowRunsQueryKey: readonly unknown[]
  resultsLabel?: string
  workflowRuns?: WorkflowRun[]
  totalPages?: number
  totalCount?: number
  countIsCapped?: boolean
  pageLimitReached?: boolean
  loading?: boolean
  showingPreviousResults?: boolean
  selectedWorkflow?: boolean
  workflowRunActionsEnabled?: boolean
  aliveChannel?: string
}

export function WorkflowRunsPage({
  owner,
  repo,
  workflowRunsQueryKey,
  resultsLabel = 'Workflow runs',
  workflowRuns,
  totalPages = 0,
  totalCount = 0,
  countIsCapped = false,
  pageLimitReached = false,
  loading = false,
  showingPreviousResults = false,
  selectedWorkflow = false,
  workflowRunActionsEnabled = true,
  aliveChannel,
}: WorkflowRunsPageProps) {
  const {query, page, filterValue, handleChange, handleSubmit, replaceQuery, updatePage, pageHref} =
    useWorkflowRunsSearch()
  const resultsRef = useRef<HTMLDivElement>(null)
  const focusAfterPaginationRef = useRef(false)
  const filterRef = useRef<FilterRef>(null)
  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference()
  const queryClient = useQueryClient()
  const proTipId = useId()
  const pageLimitHeadingId = useId()
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const track = useActionsAnalytics()

  const showDeepPaginationTip = !pageLimitReached && page >= DEEP_PAGINATION_TIP_PAGE && (workflowRuns?.length ?? 0) > 0
  const hasLiveUpdates = Boolean(aliveChannel || workflowRuns?.some(run => run.aliveChannel))
  // New runs would shift later pages. Returning to page 1 revalidates it through the route loader.
  const autoRefreshInactive = page !== 1
  const autoRefreshIntervalMs = autoRefreshInactive ? null : getAutoRefreshOption(autoRefresh).intervalMs

  const refreshNow = useCallback(async () => {
    await queryClient.invalidateQueries({queryKey: workflowRunsQueryKey, exact: true})
  }, [queryClient, workflowRunsQueryKey])

  const handleAutoRefreshChange = useCallback(
    (next: AutoRefreshOptionId) => {
      track('workflow_runs.set_auto_refresh', `ref_loc:actions_workflow_runs;interval:${next}`)
      if (next === autoRefresh) return

      setAutoRefresh(next)
      // Changing the interval drops batched notifications, so refresh once to catch up.
      if (getAutoRefreshOption(next).intervalMs !== null) void refreshNow()
    },
    [autoRefresh, refreshNow, setAutoRefresh, track],
  )

  const handleRefreshNow = useCallback(() => {
    track('workflow_runs.refresh_now', 'ref_loc:actions_workflow_runs')
    void refreshNow()
  }, [refreshNow, track])

  const handleFocusedRunRemoved = useCallback(() => {
    // Let the row and any portaled menu finish unmounting before restoring focus.
    queueMicrotask(() => {
      if (document.activeElement === document.body) resultsRef.current?.focus()
    })
  }, [])

  const handleNarrowWithCreatedFilter = useCallback(() => {
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'workflow_runs.deep_pagination_tip_narrow',
      label: 'ref_cta:deep_pagination_tip_narrow;ref_loc:actions_workflow_runs',
    })
    // Remove leading space if `filterValue` is empty and remove trailing space if present in filterValue to avoid double spacing
    const queryWithCreatedFilter = filterValue ? `${filterValue.trimEnd()} created:` : 'created:'
    filterRef.current?.setValueAndOpenSuggestions(queryWithCreatedFilter)
  }, [filterValue, sendAnalyticsEvent])

  useLayoutEffect(() => {
    if (!pageLimitReached || !focusAfterPaginationRef.current) return

    focusAfterPaginationRef.current = false
    document.getElementById(pageLimitHeadingId)?.focus()
  }, [pageLimitHeadingId, pageLimitReached])

  // `page` is a dependency so a cached page, which never flips `loading`, still moves focus.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (loading || !focusAfterPaginationRef.current) return

    focusAfterPaginationRef.current = false
    // onPageChange already scrolled to the top of the page.
    resultsRef.current?.focus({preventScroll: true})
  }, [loading, page])

  // Don't steal focus from the filter input if the query changes before the page settles.
  useEffect(() => {
    focusAfterPaginationRef.current = false
  }, [query])

  return (
    <>
      <FilteredListPageLayout.FilterBar aria-label="Filter workflow runs">
        <WorkflowRunsFilter
          owner={owner}
          repo={repo}
          filterValue={filterValue}
          onChange={handleChange}
          onSubmit={handleSubmit}
          selectedWorkflow={selectedWorkflow}
          ref={filterRef}
          aria-describedby={showDeepPaginationTip ? proTipId : undefined}
        />
      </FilteredListPageLayout.FilterBar>
      <FilteredListPageLayout.Results aria-label={resultsLabel}>
        <div ref={resultsRef} role="group" aria-label={`${resultsLabel} results`} tabIndex={-1} {...hpcAttrs()}>
          <WorkflowRunsLiveUpdates
            key={`${owner}/${repo}`}
            aliveChannel={aliveChannel}
            enabled={autoRefreshIntervalMs !== null}
            minBatchDelayMs={autoRefreshIntervalMs ?? 0}
            workflowRuns={workflowRuns ?? []}
            queryKey={workflowRunsQueryKey}
          />
          <WorkflowRunsList
            accessibleName={resultsLabel}
            allRunsPath={`/${owner}/${repo}/actions`}
            owner={owner}
            repo={repo}
            query={query}
            onQueryChange={replaceQuery}
            queryKey={workflowRunsQueryKey}
            selectedWorkflow={selectedWorkflow}
            totalCount={totalCount}
            countIsCapped={countIsCapped}
            pageLimitReached={pageLimitReached}
            pageLimitHeadingId={pageLimitHeadingId}
            workflowRuns={workflowRuns ?? []}
            loading={loading}
            showingPreviousResults={showingPreviousResults}
            actionsEnabled={workflowRunActionsEnabled}
            onFocusedRowRemoved={handleFocusedRunRemoved}
            autoRefresh={hasLiveUpdates ? autoRefresh : undefined}
            autoRefreshInactive={autoRefreshInactive}
            onAutoRefreshChange={handleAutoRefreshChange}
            onRefreshNow={handleRefreshNow}
          />
          {!pageLimitReached && totalPages > 1 && (
            <div className="d-flex flex-justify-center mt-4">
              <Pagination
                currentPage={page}
                pageCount={totalPages}
                hrefBuilder={pageHref}
                onPageChange={(event, nextPage) => {
                  track('workflow_runs.paginate', 'ref_loc:actions_workflow_runs')
                  // Let modified and middle clicks fall through to the browser.
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

                  event.preventDefault()
                  focusAfterPaginationRef.current = true
                  // The router restores each URL's saved scroll position, even on link clicks. Scrolling first saves
                  // the top of the page as this URL's position.
                  window.scrollTo({top: 0, behavior: 'instant'})
                  updatePage(nextPage)
                }}
              />
            </div>
          )}

          {showDeepPaginationTip && <DeepPaginationTip id={proTipId} onNarrow={handleNarrowWithCreatedFilter} />}
        </div>
      </FilteredListPageLayout.Results>
    </>
  )
}
